import { useCardDrift } from '../../hooks/use-card-drift';
import styles from './canvas-card.module.css';

/**
 * Floating canvas/mindmap product card for the onboarding constellation.
 * The mindmap uses token-derived colors via color-mix; no hex colors.
 * Pastel category colors are derived via local CSS vars set in the SVG attributes.
 */
export function CanvasCard() {
  const ref = useCardDrift({ amp: 11, speed: 0.11, phase: 1.7 });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={styles.card}
    >
      <div className={styles.cardhead}>
        <span className={styles.label}>Canvas</span>
        <span className={styles.windowDots}>
          <span className={styles.windowDot} />
          <span className={styles.windowDot} />
          <span className={styles.windowDot} />
        </span>
      </div>

      <div className={styles.cardbody}>
        <div className={styles.dotgrid} />

        {/* Collaborator cursor */}
        <span className={styles.cursor} aria-hidden>
          <svg className={styles.cursorIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M5 3l14 7-6 2-2 6z" />
          </svg>
          <span className={styles.cursorTag}>Euna</span>
        </span>

        {/* Mindmap SVG — colors via color-mix with tokens, no hex */}
        <svg
          className={styles.mindmap}
          viewBox="0 0 224 134"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          strokeLinecap="round"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          {/* Trunk connectors: center → 4 hubs */}
          <g stroke="color-mix(in srgb, var(--on-surface) 34%, transparent)" strokeWidth="1.6" fill="none">
            <path d="M92 60 C 74 50, 72 40, 56 38" />
            <path d="M92 74 C 74 84, 72 94, 56 96" />
            <path d="M132 60 C 150 50, 152 40, 168 38" />
            <path d="M132 74 C 150 84, 152 94, 168 96" />
          </g>

          {/* Twig connectors: hub → leaf */}
          <g stroke="color-mix(in srgb, var(--on-surface) 22%, transparent)" strokeWidth="1.2" fill="none">
            <path d="M30 38 C 22 38, 22 24, 14 22" />
            <path d="M30 96 C 22 96, 22 110, 14 112" />
            <path d="M194 38 C 202 38, 202 24, 210 22" />
            <path d="M194 96 C 202 96, 202 110, 210 112" />
          </g>

          {/* Central node */}
          <rect x="92" y="57" width="40" height="20" rx="10"
            fill="color-mix(in srgb, var(--on-surface) 14%, transparent)"
            stroke="color-mix(in srgb, var(--on-surface) 34%, transparent)"
            strokeWidth="1.4"
          />
          <line x1="99" y1="64" x2="125" y2="64"
            stroke="color-mix(in srgb, var(--on-surface) 40%, transparent)"
            strokeWidth="2"
          />
          <line x1="99" y1="70" x2="118" y2="70"
            stroke="color-mix(in srgb, var(--on-surface) 28%, transparent)"
            strokeWidth="2"
          />

          {/* 4 hubs — pastel derived from accent/primary via color-mix */}
          <g strokeWidth="1.1">
            {/* green hub (accent → green tint) */}
            <rect x="22" y="29" width="34" height="18" rx="9"
              fill="color-mix(in srgb, var(--accent) 55%, color-mix(in srgb, var(--primary) 10%, transparent))"
              stroke="color-mix(in srgb, var(--accent) 80%, transparent)"
            />
            {/* amber hub (warm accent tint) */}
            <rect x="22" y="87" width="34" height="18" rx="9"
              fill="color-mix(in srgb, var(--accent) 40%, color-mix(in srgb, var(--primary) 30%, transparent))"
              stroke="color-mix(in srgb, var(--accent) 70%, transparent)"
            />
            {/* blue hub (primary-container tint) */}
            <rect x="168" y="29" width="34" height="18" rx="9"
              fill="color-mix(in srgb, var(--primary-container) 90%, transparent)"
              stroke="color-mix(in srgb, var(--primary) 50%, transparent)"
            />
            {/* lav hub (primary+accent blend) */}
            <rect x="168" y="87" width="34" height="18" rx="9"
              fill="color-mix(in srgb, var(--accent) 60%, color-mix(in srgb, var(--primary) 40%, transparent))"
              stroke="color-mix(in srgb, var(--accent) 80%, transparent)"
            />
          </g>

          {/* Hub text lines */}
          <g stroke="color-mix(in srgb, var(--on-surface) 45%, transparent)" strokeWidth="1.6" opacity="0.55">
            <line x1="28" y1="38" x2="50" y2="38" />
            <line x1="28" y1="96" x2="50" y2="96" />
            <line x1="174" y1="38" x2="196" y2="38" />
            <line x1="174" y1="96" x2="196" y2="96" />
          </g>

          {/* 4 leaves (lighter tint, same hue as hub) */}
          <rect x="2"   y="14"  width="26" height="15" rx="7.5"
            fill="color-mix(in srgb, var(--accent) 30%, transparent)"
          />
          <rect x="2"   y="105" width="26" height="15" rx="7.5"
            fill="color-mix(in srgb, var(--accent) 25%, color-mix(in srgb, var(--primary) 10%, transparent))"
          />
          <rect x="196" y="14"  width="26" height="15" rx="7.5"
            fill="color-mix(in srgb, var(--primary-container) 70%, transparent)"
          />
          <rect x="196" y="105" width="26" height="15" rx="7.5"
            fill="color-mix(in srgb, var(--accent) 35%, transparent)"
          />
        </svg>
      </div>
    </div>
  );
}
